import { useState, useEffect } from 'preact/hooks';
import styles from './ThreadWelcomeCard.module.css';

interface ThreadWelcomeCardProps {
  threadId: string;
  threadTitle: string;
  threadType: string | null;
  threadDescription: string | null;
  templateName?: string;
  templateDescription?: string;
  phaseName?: string;
  phaseIndex?: number;
  totalPhases?: number;
  creatorName?: string;
  contributionCount?: number;
  hasContributed?: boolean;
  /**
   * Fired when the visitor explicitly dismisses onboarding ("I'm ready").
   *
   * This is the read-cursor baseline for a lurker — someone who visits, reads,
   * and leaves without contributing. Without it they never acquire a cursor, so
   * every subsequent visit is still a "first visit" and they never see a
   * Catch Me Up card at all.
   */
  onDismiss?: () => void;
}

const THREAD_TYPE_EXPLANATIONS: Record<string, string> = {
  memory: 'A Throwback is a shared memory. Multiple people share what they remember about the same moment.',
  legacy: 'An Origin Story is a collaborative life narrative told from different perspectives.',
  event: 'An Event thread captures different angles of the same experience.',
  topic: 'A Topic thread gathers different viewpoints on a shared subject.',
  milestone: 'A Milestone thread celebrates a moment from every perspective that was there.',
};

function getHeadline(threadType: string | null, creatorName?: string): string {
  const by = creatorName ? ` by ${creatorName}` : '';
  switch (threadType) {
    case 'memory':
      return `You've been invited${by} to help relive this moment`;
    case 'legacy':
      return `You've been invited${by} to help tell this story`;
    case 'event':
      return `You've been invited${by} to add your angle`;
    default:
      return `You've been invited${by} to join this collaborative story`;
  }
}

function getFlavorText(
  threadType: string | null,
  templateDescription?: string,
  threadDescription?: string | null,
): string {
  if (templateDescription) return templateDescription;
  if (threadDescription) return threadDescription;
  switch (threadType) {
    case 'memory':
      return 'Share what you remember \u2014 your take is what makes this moment real.';
    case 'legacy':
      return 'Add your piece to this story. The moments that matter to you.';
    case 'event':
      return 'Everyone saw something different. Drop yours.';
    default:
      return 'Add your perspective to this shared story.';
  }
}

export function ThreadWelcomeCard({
  threadId,
  threadTitle,
  threadType,
  threadDescription,
  templateDescription,
  phaseName,
  phaseIndex,
  totalPhases,
  creatorName,
  contributionCount = 0,
  hasContributed = false,
  onDismiss,
}: ThreadWelcomeCardProps) {
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const storageKey = `thread_welcome_seen:${threadId}`;

  useEffect(() => {
    // Only auto-dismiss if the user has contributed (not on first view)
    if (hasContributed && localStorage.getItem(storageKey)) {
      setDismissed(true);
    }
    setLoaded(true);
  }, [storageKey, hasContributed]);

  // Auto-collapse after first contribution
  useEffect(() => {
    if (hasContributed && !dismissed) {
      localStorage.setItem(storageKey, '1');
      setDismissed(true);
    }
  }, [hasContributed, dismissed, storageKey]);

  const handleDismiss = () => {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
    onDismiss?.();
  };

  const handleReopen = () => {
    localStorage.removeItem(storageKey);
    setDismissed(false);
  };

  if (!loaded) return null;

  if (dismissed) {
    return (
      <button class={styles.infoButton} onClick={handleReopen} title="How to participate">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>
    );
  }

  const headline = getHeadline(threadType, creatorName);
  const flavor = getFlavorText(threadType, templateDescription, threadDescription);
  const typeExplanation = threadType ? THREAD_TYPE_EXPLANATIONS[threadType] : null;

  return (
    <div class={styles.card}>
      <div class={styles.header}>
        <h3 class={styles.headline}>{headline}</h3>
        <button class={styles.dismissButton} onClick={handleDismiss}>
          I'm ready
        </button>
      </div>

      <p class={styles.flavorText}>{flavor}</p>

      {/* What is a "take"? */}
      <div class={styles.takeExplanation}>
        <strong>What is a &ldquo;take&rdquo;?</strong> Your version of this moment &mdash;
        a memory, a photo, a thought, or a voice note. There are no rules about what to share.
      </div>

      {/* Social proof */}
      {contributionCount > 0 && (
        <p class={styles.socialProof}>
          {contributionCount} {contributionCount === 1 ? 'take has' : 'takes have'} been shared so far
        </p>
      )}

      {/* Thread type explanation */}
      {typeExplanation && (
        <p class={styles.typeExplanation}>{typeExplanation}</p>
      )}

      <p class={styles.howItWorksLabel}>How it works</p>
      <ol class={styles.stepList}>
        <li class={styles.step}>
          <span class={styles.stepNum}>1.</span>
          <span>Read the prompts &mdash; they'll guide what to share</span>
        </li>
        <li class={styles.step}>
          <span class={styles.stepNum}>2.</span>
          <span>Add your take &mdash; text, photos, audio, or video</span>
        </li>
        <li class={styles.step}>
          <span class={styles.stepNum}>3.</span>
          <span>See other perspectives &mdash; every angle makes the story richer</span>
        </li>
      </ol>

      {phaseIndex !== undefined && totalPhases !== undefined && phaseName && (
        <p class={styles.phaseContext}>
          You're joining Phase {phaseIndex + 1} of {totalPhases}: {phaseName}
        </p>
      )}
    </div>
  );
}
